import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefCallback,
  type RefObject,
} from 'react'

export const TP_CHART_VERTICAL_HANDLE_PX = 10
export const TP_CHART_PANE_MIN_PX = 300
export const TP_FORM_PANE_MIN_PX = 200
export const TP_CHART_DEFAULT_PANE_HEIGHT_PX = 360
export const TP_EMPTY_STATE_MIN_PX = 280

interface TreatmentPlanSplitLimits {
  chartPaneMinPx: number
  formPaneMinPx: number
  defaultPaneHeightPx: number
}

function getTreatmentPlanSplitLimits(
  isEmptyState: boolean,
): TreatmentPlanSplitLimits {
  if (isEmptyState) {
    return {
      chartPaneMinPx: TP_CHART_PANE_MIN_PX,
      formPaneMinPx: TP_EMPTY_STATE_MIN_PX,
      defaultPaneHeightPx: TP_CHART_DEFAULT_PANE_HEIGHT_PX,
    }
  }
  return {
    chartPaneMinPx: TP_CHART_PANE_MIN_PX,
    formPaneMinPx: TP_FORM_PANE_MIN_PX,
    defaultPaneHeightPx: TP_CHART_DEFAULT_PANE_HEIGHT_PX,
  }
}

export function getTreatmentPlanChartAccordionClassName(
  chartVisible: boolean,
): string {
  const classes = ['treatment-plan-chart-accordion']
  if (chartVisible) {
    classes.push('treatment-plan-chart-accordion--expanded')
  }
  return classes.join(' ')
}

function measureChartChromePx(chartPane: HTMLElement): number {
  const clinicalCard = chartPane.closest('.patient-profile-clinical-chart-card')
  if (clinicalCard instanceof HTMLElement) {
    const cardRect = clinicalCard.getBoundingClientRect()
    const paneRect = chartPane.getBoundingClientRect()
    return Math.max(
      0,
      paneRect.top - cardRect.top + (cardRect.bottom - paneRect.bottom),
    )
  }

  const sticky = chartPane.closest('.treatment-plan-chart-accordion-sticky')
  if (!sticky) {
    return 0
  }
  const header = sticky.querySelector('.treatment-plan-chart-header')
  return header instanceof HTMLElement
    ? header.getBoundingClientRect().height
    : 0
}

export function clampTreatmentPlanChartPaneHeight(
  heightPx: number,
  containerHeight: number,
  chartChromePx: number,
  formPaneMinPx: number = TP_FORM_PANE_MIN_PX,
  chartPaneMinPx: number = TP_CHART_PANE_MIN_PX,
): number {
  if (containerHeight <= 0) {
    return Math.max(heightPx, chartPaneMinPx)
  }
  const maxHeight = Math.max(
    chartPaneMinPx,
    containerHeight -
      chartChromePx -
      TP_CHART_VERTICAL_HANDLE_PX -
      formPaneMinPx,
  )
  return Math.min(Math.max(heightPx, chartPaneMinPx), maxHeight)
}

export interface UseTreatmentPlanChartVerticalSplitParams {
  isSplitActive: boolean
  containerRef: RefObject<HTMLElement | null>
  isEmptyState?: boolean
}

export interface UseTreatmentPlanChartVerticalSplitResult {
  chartPaneHeightPx: number | null
  chartPaneRef: RefCallback<HTMLDivElement | null>
  onHandleMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void
}

/** Vertical split between treatment-plan chart body and plan form (height in px). */
export function useTreatmentPlanChartVerticalSplit({
  isSplitActive,
  containerRef,
  isEmptyState = false,
}: UseTreatmentPlanChartVerticalSplitParams): UseTreatmentPlanChartVerticalSplitResult {
  const draggingRef = useRef(false)
  const heightRef = useRef<number | null>(null)
  const paneTopRef = useRef(0)
  const chartPaneElRef = useRef<HTMLDivElement | null>(null)
  const prevActiveRef = useRef(false)
  const emptyStateRef = useRef(isEmptyState)
  const [chartPaneHeightPx, setChartPaneHeightPx] = useState<number | null>(
    null,
  )
  const [chartPaneMounted, setChartPaneMounted] = useState(false)

  useEffect(() => {
    heightRef.current = chartPaneHeightPx
  }, [chartPaneHeightPx])

  const chartPaneRef = useCallback((node: HTMLDivElement | null): void => {
    chartPaneElRef.current = node
    setChartPaneMounted(node !== null)
  }, [])

  useLayoutEffect(() => {
    if (!isSplitActive) {
      prevActiveRef.current = false
      emptyStateRef.current = isEmptyState
      if (heightRef.current !== null) {
        heightRef.current = null
        setChartPaneHeightPx(null)
      }
      return
    }
    if (!chartPaneMounted || !chartPaneElRef.current || !containerRef.current) {
      return
    }
    const justActivated = !prevActiveRef.current
    prevActiveRef.current = true
    const emptyChanged = emptyStateRef.current !== isEmptyState
    emptyStateRef.current = isEmptyState
    if (!justActivated && !emptyChanged && heightRef.current !== null) {
      return
    }
    const limits = getTreatmentPlanSplitLimits(isEmptyState)
    const natural = chartPaneElRef.current.offsetHeight
    const preferredHeight = isEmptyState
      ? Number.MAX_SAFE_INTEGER
      : emptyChanged || natural <= 0
        ? limits.defaultPaneHeightPx
        : natural
    const chromePx = measureChartChromePx(chartPaneElRef.current)
    const next = clampTreatmentPlanChartPaneHeight(
      preferredHeight,
      containerRef.current.clientHeight,
      chromePx,
      limits.formPaneMinPx,
      limits.chartPaneMinPx,
    )
    heightRef.current = next
    setChartPaneHeightPx(next)
  }, [isSplitActive, chartPaneMounted, containerRef, isEmptyState])

  useEffect(() => {
    const onMove = (event: MouseEvent): void => {
      if (
        !draggingRef.current ||
        !containerRef.current ||
        !chartPaneElRef.current
      ) {
        return
      }
      const limits = getTreatmentPlanSplitLimits(isEmptyState)
      const chromePx = measureChartChromePx(chartPaneElRef.current)
      const raw = event.clientY - paneTopRef.current
      const next = clampTreatmentPlanChartPaneHeight(
        raw,
        containerRef.current.clientHeight,
        chromePx,
        limits.formPaneMinPx,
        limits.chartPaneMinPx,
      )
      heightRef.current = next
      setChartPaneHeightPx(next)
    }
    const onUp = (): void => {
      if (!draggingRef.current) {
        return
      }
      draggingRef.current = false
      document.body.style.removeProperty('user-select')
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return (): void => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [containerRef, isEmptyState])

  const onHandleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault()
      if (!chartPaneElRef.current || !containerRef.current) {
        return
      }
      draggingRef.current = true
      document.body.style.userSelect = 'none'
      paneTopRef.current = chartPaneElRef.current.getBoundingClientRect().top
      const limits = getTreatmentPlanSplitLimits(isEmptyState)
      const chromePx = measureChartChromePx(chartPaneElRef.current)
      const raw = event.clientY - paneTopRef.current
      const next = clampTreatmentPlanChartPaneHeight(
        raw,
        containerRef.current.clientHeight,
        chromePx,
        limits.formPaneMinPx,
        limits.chartPaneMinPx,
      )
      heightRef.current = next
      setChartPaneHeightPx(next)
    },
    [containerRef, isEmptyState],
  )

  useEffect(() => {
    if (!isSplitActive || !chartPaneMounted || !containerRef.current) {
      return
    }
    const el = containerRef.current
    const ro = new ResizeObserver(() => {
      if (draggingRef.current || heightRef.current === null) {
        return
      }
      const limits = getTreatmentPlanSplitLimits(isEmptyState)
      const chromePx = chartPaneElRef.current
        ? measureChartChromePx(chartPaneElRef.current)
        : 0
      const next = clampTreatmentPlanChartPaneHeight(
        heightRef.current,
        el.clientHeight,
        chromePx,
        limits.formPaneMinPx,
        limits.chartPaneMinPx,
      )
      if (next === heightRef.current) {
        return
      }
      heightRef.current = next
      setChartPaneHeightPx(next)
    })
    ro.observe(el)
    return (): void => {
      ro.disconnect()
    }
  }, [isSplitActive, chartPaneMounted, containerRef, isEmptyState])

  return {
    chartPaneHeightPx,
    chartPaneRef,
    onHandleMouseDown,
  }
}
